/**
 * Pricing Formula Module
 *
 * Pure computation — no DB access. Works in both backend and frontend.
 * AI pricing agent reads FORMULA_PARAMS and proposes optimized values.
 *
 * Formula: price = MAX((prodCost × MATERIAL_MARGIN + CRAFT_BASE) × (1 + complexity × COMPLEXITY_FACTOR) × BUSINESS_OVERHEAD, PRICE_FLOOR)
 *
 * Key insight: artisan jewelry value comes from design/craft (fixed) + material (variable).
 * A pure multiplicative model doesn't fit because markup ratios range from 1.5× to 16×.
 * The additive CRAFT_BASE separates artisan value from material cost.
 */

// ── Material classification ──────────────────────────────────────────

export type MaterialCategory = 'silver' | 'bronze' | 'copper_patina' | 'gold_plating' | 'unknown';

/**
 * Classify material string into a pricing category.
 * Uses case-insensitive substring matching (mirrors Excel SEARCH()).
 */
export function classifyMaterial(material: string): MaterialCategory {
  const m = (material || '').toLowerCase();
  if (m.includes('gold') && (m.includes('plat') || m.includes('gilded') || m.includes('vermeil'))) return 'gold_plating';
  if (m.includes('silver') || m.includes('argento')) return 'silver';
  if (m.includes('copper') || m.includes('patina') || m.includes('rame')) return 'copper_patina';
  if (m.includes('bronze') || m.includes('bronzo') || m.includes('brass') || m.includes('ottone')) return 'bronze';
  // Gold keywords without plating indicator → treat as gold plating (most common for this brand)
  if (m.includes('gold') || m.includes('oro')) return 'gold_plating';
  return 'unknown';
}

// ── Formula parameters (AI reads + proposes changes) ─────────────────

export const FORMULA_PARAMS = {
  // Material cost margin — markup on raw material cost (covers waste, sourcing)
  MATERIAL_MARGIN: 0.8,

  // Base artisan craft value — fixed per piece (covers labor, design, brand value)
  // This is the key differentiator from pure material-markup formulas.
  // Most of the price for artisan jewelry comes from craft, not material.
  CRAFT_BASE: 25,

  // Complexity factor — each complexity point (1-10) adds this % to the total price
  // c=3 → ×1.45, c=5 → ×1.75, c=7 → ×2.05, c=9 → ×2.35
  COMPLEXITY_FACTOR: 0.15,

  // Business overhead — final multiplier covering CAC, platform fees, marketing
  // Breakdown: Stripe ~3%, marketing/CAC ~12-15%, operational ~7-10%
  BUSINESS_OVERHEAD: 1.25,

  // Minimum price floor (no product should be recommended below this)
  PRICE_FLOOR: 50,
} as const;

// ── Production cost ──────────────────────────────────────────────────

/**
 * Calculate production cost from weight and material cost per gram.
 * Returns null if either input is missing/invalid.
 */
export function calculateProductionCost(
  weightGrams: number | null | undefined,
  materialCostPerGram: number | null | undefined,
): number | null {
  if (weightGrams == null || materialCostPerGram == null) return null;
  if (weightGrams <= 0 || materialCostPerGram <= 0) return null;
  return Math.round(weightGrams * materialCostPerGram * 100) / 100;
}

// ── Recommended price ────────────────────────────────────────────────

export interface FormulaInput {
  productionCost: number;
  material: string;
  complexity: number | null | undefined;
  oneOfKind?: boolean;
}

export interface FormulaBreakdown {
  materialComponent: number;
  craftBase: number;
  baseValue: number;
  complexityMultiplier: number;
  afterComplexity: number;
  overheadMultiplier: number;
  complexity: number;
}

export interface FormulaResult {
  productionCost: number;
  recommendedPrice: number;
  materialCategory: MaterialCategory;
  formulaBranch: string;
  oneOfKind: boolean;
  breakdown: FormulaBreakdown;
  skipReason?: string;
}

/**
 * Calculate recommended selling price from production cost, material, and complexity.
 *
 * Formula (optimized against 43 real products, ~60% within ±30%):
 *   price = MAX((prodCost × 0.8 + €25) × (1 + complexity × 0.15) × 1.25, €50)
 *
 * The formula is a conservative baseline — the AI pricing agent analyzes outliers
 * and proposes adjustments for pieces with high artisan value (Langosta, Pradera)
 * or entry-point pricing (Circo, Lava Mini).
 *
 * One-of-a-kind: flag passed through for AI analysis (no hardcoded premium).
 */
export function calculateRecommendedPrice(input: FormulaInput): FormulaResult {
  const { productionCost, material, complexity, oneOfKind } = input;
  const P = FORMULA_PARAMS;
  const cat = classifyMaterial(material);
  const c = complexity ?? 5; // default to mid-complexity if not set

  const baseValue = productionCost * P.MATERIAL_MARGIN + P.CRAFT_BASE;
  const complexityMultiplier = 1 + c * P.COMPLEXITY_FACTOR;
  let price = baseValue * complexityMultiplier * P.BUSINESS_OVERHEAD;

  let branch: string = cat;
  if (price < P.PRICE_FLOOR) {
    price = P.PRICE_FLOOR;
    branch = 'floor';
  }

  // Round to nearest euro
  price = Math.round(price);

  return {
    productionCost,
    recommendedPrice: price,
    materialCategory: cat,
    formulaBranch: branch,
    oneOfKind: Boolean(oneOfKind),
    breakdown: {
      materialComponent: Math.round(productionCost * P.MATERIAL_MARGIN * 100) / 100,
      craftBase: P.CRAFT_BASE,
      baseValue: Math.round(baseValue * 100) / 100,
      complexityMultiplier: Math.round(complexityMultiplier * 100) / 100,
      afterComplexity: Math.round(baseValue * complexityMultiplier * 100) / 100,
      overheadMultiplier: P.BUSINESS_OVERHEAD,
      complexity: c,
    },
  };
}
